/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview 90% in-person rule for enabling virtual appointment conversion on ViewAppointments.
 * @module logic/appointments/virtualAppointmentEligibility
 */

import { isVirtualAppointment } from "../../utils/calendar/calendarAppointmentUtils.js";

const IN_PERSON_THRESHOLD = 0.9;

/**
 * Whether a requirement's scheduled appointments meet the in-person threshold for virtual conversion.
 * @param {Object|null|undefined} request
 * @param {Array<Object>} appointments All calendar appointments for the current scope
 * @returns {boolean}
 */
export const canMakeVirtualAppointment = (request, appointments) => {
  if (typeof request?.canMakeVirtual === "boolean") {
    return request.canMakeVirtual;
  }

  if (!request?.idRequerimiento) {
    return false;
  }

  const requirementAppointments = appointments.filter(
    (appointment) => appointment.idRequerimiento === request.idRequerimiento,
  );

  const setAppointments = requirementAppointments.filter(
    (appointment) =>
      appointment.estado === "approved" &&
      appointment.horaInicio &&
      appointment.horaFin,
  );

  if (setAppointments.length === 0) {
    return true;
  }

  const inPersonAppointments = setAppointments.filter(
    (appointment) => !isVirtualAppointment(appointment),
  );

  return (
    inPersonAppointments.length / setAppointments.length >= IN_PERSON_THRESHOLD
  );
};
